import * as Sentry from "@sentry/react"
import { AnimatePresence } from "framer-motion"
import * as React from "react"
import { Col, Container, Hidden, Row, Visible } from "react-grid-system"
import Markdown from "react-markdown"
import { Link } from "react-router-dom"
import styled from "styled-components"
import { MediaQuery } from "../../../utils/design-helpers"
import { useInView, useScrollToRef } from "../../../utils/helpers"
import NavigationTextButton from "../../atoms/buttons/NavigationTextButton"
import SecondaryButton from "../../atoms/buttons/SecondaryButton"
import SmallColorfulButton from "../../atoms/buttons/SmallColorfulButton"
import TextButton from "../../atoms/buttons/TextButton"
import { ArrowRight } from "../../atoms/icons/ArrowRight"
import { ContactPhoneIcon } from "../../atoms/icons/ContactPhoneIcon"
import { MailIcon } from "../../atoms/icons/MailIcon"
import LoadingSpinner from "../../atoms/LoadingSpinner"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import H2 from "../../atoms/typography/H2"
import H3 from "../../atoms/typography/H3"
import H4 from "../../atoms/typography/H4"
import Body from "../../atoms/typography/Body"
import H1 from "../../atoms/typography/H1"
import Modal from "../../molecules/Modals/Modal"
import PreOrderModalContent from "../../molecules/Modals/Motion/PreOrderModalContent"
import SubscribeToUpdatesModalContent from "../../molecules/Modals/Motion/SubscribeToUpdatesModalContent"
import { DownloadProductSheetButton } from "../../molecules/store/DownloadProductSheet"
import UspCarousel from "../../molecules/store/ProductPages/UspCarousel"
import { motionUspData } from "../../molecules/store/ProductPages/uspData/motion"
import { RequestDemoButton } from "../../molecules/store/RequestDemo"
import { theme } from "../../theme"
import { useProductPageCollection } from "../../../hooks/products/useProductPageCollection"
import UspCarouselTwo from "../../contentful/UspCarouselTwo"
import { useUspCarousel } from "../../../hooks/products/useUspCarousel"

const ProductImage = styled.img`
  width: 120%;
  object-fit: cover;
  height: 120%;
`
const ColoredTitle = styled(H1)`
  background: ${(props) => props.theme.gradients.red};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
`

const FlexRowColSmall = styled(Col)`
  display: flex;
  flex-direction: column;
  padding: 16px;
  justify-content: space-between;
  height: 322px;

  ${MediaQuery.XS} {
    height: 280px;
  }
`

const FooterImage = styled.img`
  width: 100%;
  height: 259px;
  object-fit: cover;
`

const FooterContainer = styled.div`
  position: absolute;
  top: 25%;
  left: 0;
  padding: 1rem;
  width: 100%;
`

const ContactSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  cursor: pointer;

  :hover {
    filter: opacity(0.6);
  }
`

const MapCoverageImage = styled.img`
  width: 100%;
  height: 500px;
  object-fit: cover;
  border-radius: 24px;

  ${MediaQuery.XS} {
    height: 429px;
    margin-bottom: 3rem;
  }
`

const ExplodedImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;

  ${MediaQuery.XS} {
    margin-top: 1rem;
  }
`

const CarTextSection = styled.div`
  position: relative;
  margin-bottom: 155px;

  ${MediaQuery.XS} {
    margin-bottom: 30px;
  }
`

const FixedBottomMenu = styled.div`
  width: 100%;
  padding: 0.5rem;
  background: rgba(250, 250, 250, 0.8);
  backdrop-filter: blur(3px);
  position: sticky;
  left: 0;
  top: 75px;
  z-index: 4;
  box-shadow: 0px 1px 6px 0px rgba(119, 119, 119, 0.25);

  ${MediaQuery.XS} {
    top: 55px;
  }
`

const MenuNavigationContainer = styled.div`
  display: flex;
  justify-content: space-around;
  flex: 0.5;
  align-items: center;
`

const NAVBAR_OFFSET = 150

const MotionProductPage: React.FC = () => {
  const [preOrderOpen, setPreOrderOpen] = React.useState(false)
  const [signUpOpen, setSignUpOpen] = React.useState(false)
  const preOrderAvailable = false

  const { productPages, error, loading } = useProductPageCollection()
  const productPage = productPages?.items?.find((x: any) => x.title === "Motion")

  if (error) {
    Sentry.captureException(`Error fetching product pages for ku, ${error}`)
  }

  const overviewRef = React.useRef<HTMLDivElement>(null)
  const applicationsRef = React.useRef<HTMLDivElement>(null)
  const specRef = React.useRef<HTMLDivElement>(null)
  const bottomRef = React.useRef<HTMLDivElement>(null)

  const overviewRefIsInView = useInView(overviewRef)
  const applicationsRefIsInView = useInView(applicationsRef)
  const specRefIsInView = useInView(specRef)

  const uspCarousel = useUspCarousel("7oeZJv5IxCKEy3QHIG40bj")

  const scrollToRef = useScrollToRef(NAVBAR_OFFSET)

  React.useEffect(() => {
    if (overviewRefIsInView && !applicationsRefIsInView && !specRefIsInView) {
      setActiveSection(0)
    } else if (!overviewRefIsInView && applicationsRefIsInView && !specRefIsInView) {
      setActiveSection(1)
    } else if (!overviewRefIsInView && !applicationsRefIsInView && specRefIsInView) {
      setActiveSection(2)
    }
  }, [overviewRefIsInView, applicationsRefIsInView, specRefIsInView])

  const [activeSection, setActiveSection] = React.useState(0)

  React.useEffect(() => {
    document.title = "Motion Product"
  }, [])

  const scrollToOverview = () => {
    scrollToRef(overviewRef)
  }
  const scrollToApplications = () => {
    scrollToRef(applicationsRef)
  }
  const scrollToSpecifications = () => {
    scrollToRef(specRef)
  }

  const openPreOrder = () => {
    setPreOrderOpen(true)
  }
  const openSignUp = () => {
    setSignUpOpen(true)
  }

  return (
    <div style={{ position: "relative" }}>
      <FixedBottomMenu>
        <Container>
          <Row nogutter justify="between" align="center">
            <H2 color={theme.colors.dark}>Satcube Motion</H2>
            <Hidden xs sm md>
              <MenuNavigationContainer>
                <NavigationTextButton
                  title="Overview"
                  onClick={scrollToOverview}
                  active={activeSection === 0}
                  color={theme.colors.dark}
                  subMenu
                />
                <Spacer spacing={1} />
                <NavigationTextButton
                  title="Applications"
                  onClick={scrollToApplications}
                  active={activeSection === 1}
                  color={theme.colors.dark}
                  subMenu
                />
                <Spacer spacing={1} />
                <NavigationTextButton
                  title="Specifications"
                  onClick={scrollToSpecifications}
                  active={activeSection === 2}
                  color={theme.colors.dark}
                  subMenu
                />
              </MenuNavigationContainer>
            </Hidden>

            <SmallColorfulButton
              title="Stay updated"
              variant="red"
              iconRight={<ArrowRight color="white" />}
              onClick={openPreOrder}
            />
          </Row>
        </Container>
      </FixedBottomMenu>
      <div ref={overviewRef} id="overviewSection" />
      <div
        style={{
          backgroundImage:
            "url('https://images.ctfassets.net/kzqg847wz5jr/4c0pSc41wpFQe7k9otAxKK/92739ac7f2de45180904f91174ade141/Motion-header.webp')",
          backgroundSize: "cover",
        }}
      >
        <HorizontalSpacer spacing={3} />
        <Hidden xxl xxxl>
          <HorizontalSpacer spacing={10} />
        </Hidden>
        <Visible xxl xxxl>
          <HorizontalSpacer spacing={12} />
        </Visible>
        <Container>
          <Row align="center">
            <Col lg={6} style={{ padding: 16 }}>
              {loading && <LoadingSpinner />}

              <HorizontalSpacer spacing={1} />
              {/* <H4 textAlign="center">{productPage?.promoTitleOne}</H4> */}
              <H1 color={theme.colors.white} textAlign="center">
                {productPage?.promoTitleGradient}
              </H1>
              <HorizontalSpacer spacing={1} />
              <H4 color={theme.colors.white} textAlign="center">
                {productPage?.promoSubtitle}
              </H4>
              <HorizontalSpacer spacing={1} />
              <Row align="center" justify="center" nogutter>
                <DownloadProductSheetButton productSheetType="satcube_motion" buttonType="colorred" />
                <Hidden xs sm md lg>
                  <Visible xl xxl xxxl>
                    <Spacer spacing={2.25} />
                  </Visible>
                  <SecondaryButton title="Stay updated" onClick={openSignUp} icon={<MailIcon color="white" />} />
                </Hidden>
              </Row>
              <Visible lg>
                <HorizontalSpacer spacing={2} />
                <Row justify="center">
                  <SecondaryButton title="Stay updated" onClick={openSignUp} icon={<MailIcon color="white" />} />
                </Row>
              </Visible>
              <Visible xs sm md>
                <HorizontalSpacer spacing={2} />
                <Row justify="center">
                  <SecondaryButton title="Stay updated" onClick={openSignUp} icon={<MailIcon color="white" />} />
                </Row>
              </Visible>
            </Col>
          </Row>
        </Container>
        <Hidden xxl xxxl>
          <HorizontalSpacer spacing={10} />
        </Hidden>
        <Visible xxl xxxl>
          <HorizontalSpacer spacing={12} />
        </Visible>
      </div>
      <HorizontalSpacer spacing={2} />

      <CarTextSection>
        <Container>
          <Col lg={6}>
            <ColoredTitle>Our new on-the-move terminal</ColoredTitle>
          </Col>
          <HorizontalSpacer spacing={2.5} />
          <Row>
            <Col lg={4}>
              <Body>
                Introducing the smallest and most advanced mobility terminal on the market, crafted with Satcube’s
                expertise in robust and intuitive design. Currently in development, the Satcube Motion terminal is
                easily mountable on any vehicle due to its low weight and compact size, and runs on standard vehicle
                batteries. Its user-friendly setup and best-in-class user experience makes it accessible to anyone,
                without need for prior training.
                <br />
                <br />
                Designed to work on both Intelsat’s GEO network Flexmove and Satcubes Bespoke Networks services, the
                terminal can be used globally, even in the most contested environments. With a low profile, MIL-STD 810
                and 461, IP67 certification, the Satcube Motion is built to withstand the harshest conditions, making it
                ideal for mission-critical operations. Experience the future of mobile connectivity with Satcube Motion.
                <br />
                <br />
                <TextButton title="Pre-order available from Autumn 2025." onClick={openPreOrder} />
              </Body>
            </Col>
          </Row>
        </Container>

        <Hidden xs sm md>
          <div style={{ position: "absolute", top: 0, right: 0, bottom: 0 }}>
            <img
              style={{ objectFit: "cover", width: "100%" }}
              src="https://images.ctfassets.net/kzqg847wz5jr/4Ek9E5TTwn7YfzEsBWskSY/d86f49d483ad937109fd99652df2188b/Ford_Raptor_persp_daylight_1_-extended-website-transparent_2_copy.webp"
            />
          </div>
        </Hidden>
      </CarTextSection>

      <Visible xs sm md>
        <img
          style={{ objectFit: "cover", width: "100%" }}
          src="https://images.ctfassets.net/kzqg847wz5jr/4Ek9E5TTwn7YfzEsBWskSY/d86f49d483ad937109fd99652df2188b/Ford_Raptor_persp_daylight_1_-extended-website-transparent_2_copy.webp"
        />
        <HorizontalSpacer spacing={2} />
      </Visible>

      <div style={{ backgroundColor: "white", height: "100%", width: "100%" }}>
        <Container>
          <HorizontalSpacer spacing={8} />
          {/* <UspCarousel color={theme.gradients.red} isMotion numberOfSteps={3} data={motionUspData} /> */}
          {uspCarousel.data && <UspCarouselTwo textColor="dark" uspCarousel={uspCarousel.data} />}
          <HorizontalSpacer spacing={4} />
        </Container>
      </div>

      <HorizontalSpacer spacing={6} />
      <div ref={applicationsRef} id="applicationsSection" />
      <Container>
        <Row gutterWidth={32}>
          <Col lg={6}>
            <MapCoverageImage src="https://images.ctfassets.net/kzqg847wz5jr/2mjy2DQ9bNYJRMj5Lg4hK1/11b4b71f1682db1ab99ea072d875207c/Motion_-_above.webp" />
            <HorizontalSpacer spacing={2} />
          </Col>
          <Col lg={6}>
            <ColoredTitle>{productPage?.internetCoverageTitle}</ColoredTitle>
            <HorizontalSpacer spacing={2} />
            <Body>
              <Markdown>{productPage?.internetCoverageText}</Markdown>
            </Body>
          </Col>
        </Row>
      </Container>

      <HorizontalSpacer spacing={8} />

      <Container>
        <Row gutterWidth={32}>
          <Visible xs sm md>
            <Col lg={6}>
              <MapCoverageImage src="https://images.ctfassets.net/kzqg847wz5jr/7cnk9jsSZNTpt1NCrre95Y/a218d63c0670fe342b4dfb5620bb7cf2/Motion_app-new-latancy.webp" />
              <HorizontalSpacer spacing={2} />
            </Col>
          </Visible>
          <Col lg={6}>
            <ColoredTitle>Full control from the device of your choice</ColoredTitle>
            <HorizontalSpacer spacing={2} />
            <Body>
              The Satcube Motion will be fully controllable through Satcube’s web based user interface, enabling you to
              monitor signal strength, satellite details, pointing, and all other information you might need. Technical
              documentation, user guides, and FAQs are available offline in the interface, and our support team will
              only be a click away. 
            </Body>
          </Col>
          <Hidden xs sm md>
            <Col lg={6}>
              <MapCoverageImage src="https://images.ctfassets.net/kzqg847wz5jr/7u1HbjUMMEimE0I9wo3rS8/430b61d0fa51caf058bd4dfd76b65c2a/Motion_-_webGUI.webp" />
            </Col>
          </Hidden>
        </Row>
        <HorizontalSpacer spacing={8} />
      </Container>

      <div ref={specRef} id="specsSection" style={{ width: "100%", height: "100%", backgroundColor: "white" }}>
        <HorizontalSpacer spacing={4} />
        <Container>
          <Row align="center">
            <Col lg={1} />
            <Col lg={5}>
              <ExplodedImage src="https://images.ctfassets.net/kzqg847wz5jr/1T3d3blO3ZXC6Xq56oQQ6L/28833856c87159234592209d156a468a/Motion_-_specs.webp" />
            </Col>
            <Col lg={5}>
              <ColoredTitle>Handmade hightech</ColoredTitle>
              <H2>Technical specifications</H2>
            </Col>
          </Row>
          <HorizontalSpacer spacing={4} />
          <Row>
            <FlexRowColSmall lg={4}>
              <div>
                <H3>Dimensions</H3>
                <HorizontalSpacer spacing={0.5} />
                <Body>15 kg; 500 x 500 x 290 mm</Body>
                <HorizontalSpacer spacing={0.25} />
                <Body>33 lbs; 19.7 x 19.7 x 11.4 inch</Body>
              </div>
              <div>
                <H3>Environmental rating</H3>
                <HorizontalSpacer spacing={0.5} />
                <Body>IP67 water and dust proof</Body>
              </div>
              <div>
                <H3>Scan angles</H3>
                <HorizontalSpacer spacing={0.5} />
                <Body>Elevation: 15° - 90°</Body>
                <HorizontalSpacer spacing={0.25} />
                <Body>Azimuth: continuous 360˚</Body>
              </div>
              <div>
                <H3>Power</H3>
                <HorizontalSpacer spacing={0.5} />
                <Body>48 VDC, 150W</Body>
                <HorizontalSpacer spacing={0.25} />
                <Body>AC/DC 100 - 240 VAC</Body>
                <HorizontalSpacer spacing={0.25} />
                <Body>47 - 63 Hz</Body>
              </div>
            </FlexRowColSmall>
            <FlexRowColSmall lg={4}>
              <div>
                <H3>Antenna parameters</H3>
                <HorizontalSpacer spacing={0.5} />
                <Body>Tx: 13.75 - 14.50 GHz (Ku band)</Body>
                <HorizontalSpacer spacing={0.25} />
                <Body>Rx: 10.95 - 12.75 GHz (Ku band)</Body>
                <HorizontalSpacer spacing={0.25} />
                <Body>Linear polarisation</Body>
                <HorizontalSpacer spacing={0.25} />
                <Body>EIRP: 47.5 dBW - All scan angles</Body>
                <HorizontalSpacer spacing={0.25} />
                <Body>G/T: 9 db/K - All scan angles</Body>
              </div>
              <div>
                <H3>Internal modem</H3>
                <HorizontalSpacer spacing={0.5} />
                <Body>iDirect IQ200</Body>
              </div>
              <div>
                <H3>Approvals</H3>
                <HorizontalSpacer spacing={0.5} />
                <Body>CE - Pending</Body>
                <HorizontalSpacer spacing={0.25} />
                <Body>FCC - Pending</Body>
              </div>
            </FlexRowColSmall>
            <FlexRowColSmall lg={4}>
              <div>
                <H3>Wide temperature range</H3>
                <HorizontalSpacer spacing={0.5} />
                <Body>-46°C to 49°C/-51°F to 121°F Solar radiation 1120W/m2 MIL-STD-810H compliant</Body>
              </div>
              <div>
                <H3>Data rate</H3>
                <HorizontalSpacer spacing={0.5} />
                <Body>Up to 85/20 Mbps via HTS Satellite</Body>
                <HorizontalSpacer spacing={0.25} />
                <Body>Full duplex</Body>
              </div>
              <div>
                <H3>Connections</H3>
                <HorizontalSpacer spacing={0.5} />
                <Body>2x RJ45 15W PoE Connectors</Body>
                <HorizontalSpacer spacing={0.25} />
                <Body>Web-based management</Body>
                <HorizontalSpacer spacing={0.25} />
                <Body>In car unit available with 2 x RJ45</Body>
                <HorizontalSpacer spacing={0.25} />
                <Body>Easy installation multi cable assembly</Body>
              </div>
            </FlexRowColSmall>
          </Row>
          <HorizontalSpacer spacing={6.5} />
          <Row nogutter justify="center" align="center">
            <DownloadProductSheetButton productSheetType="satcube_motion" buttonType="colorred" />
            <Hidden xs md>
              <Spacer spacing={3} />
              <Link to="/store#ku">
                <SecondaryButton title="Discover more products" iconRight={<ArrowRight color="white" />} />
              </Link>
            </Hidden>
          </Row>

          <Visible xs md>
            <HorizontalSpacer spacing={2} />
            <Row justify="center">
              <Link to="/store#ku">
                <SecondaryButton title="Discover more products" iconRight={<ArrowRight color="white" />} />
              </Link>
            </Row>
          </Visible>

          <HorizontalSpacer spacing={6} />
          <Body textAlign="center">Product currently under development. Specifications are subject to change.</Body>

          <HorizontalSpacer spacing={6} />
        </Container>
      </div>
      <div ref={bottomRef} />

      <div style={{ position: "relative", width: "100%" }}>
        <FooterImage src="https://images.ctfassets.net/kzqg847wz5jr/QiS1bwH9sUDQXvv5c8uDJ/a86734801ef998e87a9c1373681c4fcd/Component_128.png" />
        <FooterContainer>
          <Container>
            <Row justify="between">
              <div>
                <H1 color="white">Want to know more?</H1>
                <RequestDemoButton productType="satcube_ku" buttonType="textwhite" />
                <Visible xs sm md>
                  <HorizontalSpacer spacing={2} />
                </Visible>
              </div>
              <Link to="/contact">
                <ContactSection>
                  <ContactPhoneIcon color="white" />
                  <Spacer spacing={1} />
                  <H3 color="white">Contact sales</H3>
                </ContactSection>
              </Link>
            </Row>
          </Container>
        </FooterContainer>
      </div>

      {preOrderOpen && (
        <AnimatePresence initial={false} mode="wait" onExitComplete={() => null}>
          <Modal open={preOrderOpen} onClose={() => setPreOrderOpen(false)} closeIcon>
            {preOrderAvailable ? (
              <PreOrderModalContent onClose={() => setPreOrderOpen(false)} />
            ) : (
              <SubscribeToUpdatesModalContent onClose={() => setPreOrderOpen(false)} />
            )}
          </Modal>
        </AnimatePresence>
      )}
      {signUpOpen && (
        <AnimatePresence initial={false} mode="wait" onExitComplete={() => null}>
          <Modal open={signUpOpen} onClose={() => setSignUpOpen(false)} closeIcon>
            <SubscribeToUpdatesModalContent onClose={() => setSignUpOpen(false)} />
          </Modal>
        </AnimatePresence>
      )}
    </div>
  )
}

export default MotionProductPage
